import { useEffect, useState, useRef } from "react";
import { collection, addDoc, deleteDoc, doc, query, where, getDocs, serverTimestamp, orderBy } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { useAuth } from "../../contexts/AuthContext";
import { Plus, Trash2, Music, Clock, Upload, CheckCircle, AlertCircle, X, ChevronDown, ChevronUp, Loader2, Library, Search } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

function formatTime(seconds) {
  if (!seconds) return "0:00";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return h > 0 ? `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}` : `${m}:${s.toString().padStart(2, "0")}`;
}

function TrackUploader({ onTrackAdded }) {
  const { user } = useAuth();
  const [tab, setTab] = useState("upload"); // "upload" | "library"
  const [queue, setQueue] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef(null);

  // Library state
  const [library, setLibrary] = useState([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [librarySearch, setLibrarySearch] = useState("");
  const [addedIds, setAddedIds] = useState(new Set()); // track which were added this session

  const loadLibrary = async () => {
    if (!user) return;
    setLibraryLoading(true);
    try {
      const q = query(collection(db, "audioUploads"), where("creatorId", "==", user.uid));
      const snap = await getDocs(q);
      const tracks = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      // Sort client-side to avoid orderBy requiring a composite index
      tracks.sort((a, b) => (b.uploadedAt?.seconds || 0) - (a.uploadedAt?.seconds || 0));
      setLibrary(tracks);
    } catch (e) {
      console.error("Library load error:", e);
    }
    setLibraryLoading(false);
  };

  const switchToLibrary = () => {
    setTab("library");
    if (library.length === 0) loadLibrary();
  };

  const addFromLibrary = (track) => {
    onTrackAdded({
      title: track.title || track.originalFilename || "Track",
      artist: track.artist || "",
      mp3FileUrl: track.mp3FileUrl,
      storageURL: track.mp3FileUrl,
      durationSeconds: track.durationSeconds || 0,
    });
    setAddedIds((s) => new Set([...s, track.id]));
  };

  const addFiles = (files) => {
    const items = Array.from(files)
      .filter((f) => f.type.startsWith("audio/") || /\.(mp3|wav|flac|ogg|aac|m4a)$/i.test(f.name))
      .map((f) => ({
        id: Math.random().toString(36).slice(2),
        file: f,
        title: f.name.replace(/\.[^/.]+$/, "").replace(/[_-]+/g, " ").trim(),
        status: "pending",
        error: null,
      }));
    setQueue((q) => [...q, ...items]);
  };

  const uploadAll = async () => {
    const pending = queue.filter((x) => x.status === "pending");
    if (!pending.length) return;
    setIsProcessing(true);
    const idToken = await user.getIdToken(true);
    for (const item of pending) {
      setQueue((q) => q.map((x) => x.id === item.id ? { ...x, status: "uploading" } : x));
      try {
        const formData = new FormData();
        formData.append("file", item.file);
        formData.append("track_title", item.title.trim() || item.file.name);
        formData.append("show_id", "");
        const res = await fetch(`${API_URL}/api/audio/upload`, {
          method: "POST",
          headers: { Authorization: `Bearer ${idToken}` },
          body: formData,
        });
        const text = await res.text();
        let data;
        try { data = JSON.parse(text); } catch { throw new Error(`Server error (${res.status})`); }
        if (!res.ok) throw new Error(data.detail || "Upload failed");
        onTrackAdded({
          title: data.metadata?.title || item.title,
          artist: data.metadata?.artist || "",
          mp3FileUrl: data.downloadURL,
          storageURL: data.downloadURL,
          durationSeconds: data.metadata?.durationSeconds || 0,
        });
        setQueue((q) => q.map((x) => x.id === item.id ? { ...x, status: "done" } : x));
        // Refresh library so new track appears there too
        setLibrary([]);
      } catch (e) {
        setQueue((q) => q.map((x) => x.id === item.id ? { ...x, status: "error", error: e.message } : x));
      }
    }
    setIsProcessing(false);
  };

  const pendingCount = queue.filter((x) => x.status === "pending").length;
  const allDone = queue.length > 0 && queue.every((x) => x.status === "done" || x.status === "error");

  const filteredLibrary = library.filter((t) => {
    const q = librarySearch.toLowerCase();
    return !q || (t.title || "").toLowerCase().includes(q) || (t.artist || "").toLowerCase().includes(q);
  });

  return (
    <div className="bg-[#1a1a1a] rounded-xl overflow-hidden">
      {/* Tabs */}
      <div className="flex border-b border-white/5">
        <button type="button" onClick={() => setTab("upload")}
          className={`flex items-center gap-1.5 px-4 py-2 text-xs font-semibold transition-colors ${tab === "upload" ? "text-[#00ff77] border-b-2 border-[#00ff77]" : "text-zinc-500 hover:text-zinc-300"}`}>
          <Upload size={11} /> Upload New
        </button>
        <button type="button" onClick={switchToLibrary}
          className={`flex items-center gap-1.5 px-4 py-2 text-xs font-semibold transition-colors ${tab === "library" ? "text-[#00ff77] border-b-2 border-[#00ff77]" : "text-zinc-500 hover:text-zinc-300"}`}>
          <Library size={11} /> My Library
        </button>
      </div>

      <div className="p-3 space-y-2">
        {/* ── Upload Tab ── */}
        {tab === "upload" && (
          <>
            <div onClick={() => fileInputRef.current?.click()}
              className="border border-dashed border-white/10 rounded-lg px-3 py-2.5 text-xs text-zinc-500 cursor-pointer hover:border-[#00ff77]/30 text-center transition-colors">
              <Upload size={13} className="mx-auto mb-1 opacity-50" />
              Click to add audio files — select multiple at once
              <input ref={fileInputRef} type="file" accept="audio/*" multiple className="hidden"
                onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
            </div>

            {queue.length > 0 && (
              <div className="space-y-1 max-h-36 overflow-y-auto">
                {queue.map((item) => (
                  <div key={item.id} className="flex items-center gap-2 bg-[#111] rounded-lg px-2 py-1.5">
                    {item.status === "uploading" && <Loader2 size={11} className="text-[#00ff77] animate-spin flex-shrink-0" />}
                    {item.status === "done" && <CheckCircle size={11} className="text-[#00ff77] flex-shrink-0" />}
                    {item.status === "error" && <AlertCircle size={11} className="text-red-400 flex-shrink-0" />}
                    {item.status === "pending" && <Music size={11} className="text-zinc-600 flex-shrink-0" />}
                    <input value={item.title}
                      onChange={(e) => setQueue((q) => q.map((x) => x.id === item.id ? { ...x, title: e.target.value } : x))}
                      disabled={item.status !== "pending"}
                      className="flex-1 bg-transparent text-white text-xs focus:outline-none disabled:text-zinc-500 min-w-0" />
                    {item.status === "error" && (
                      <span className="text-red-400 text-[9px] truncate max-w-[160px]" title={item.error}>{item.error}</span>
                    )}
                    {item.status === "uploading" && <span className="text-[#00ff77] text-[9px]">Converting…</span>}
                    {item.status === "pending" && (
                      <button onClick={() => setQueue((q) => q.filter((x) => x.id !== item.id))} type="button" className="text-zinc-600 hover:text-red-400"><X size={10} /></button>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-2">
              {pendingCount > 0 && (
                <button onClick={uploadAll} type="button" disabled={isProcessing} data-testid="track-upload-btn"
                  className="flex-1 flex items-center justify-center gap-1.5 bg-[#00ff77] text-black font-bold text-xs px-3 py-2 rounded-lg hover:bg-[#00e66b] transition-colors disabled:opacity-50">
                  {isProcessing ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />}
                  {isProcessing ? "Uploading…" : `Add ${pendingCount} Track${pendingCount > 1 ? "s" : ""}`}
                </button>
              )}
              {allDone && <button type="button" onClick={() => setQueue([])} className="text-xs text-zinc-500 hover:text-zinc-300 px-2">Clear</button>}
            </div>
          </>
        )}

        {/* ── Library Tab ── */}
        {tab === "library" && (
          <div className="space-y-2">
            {/* Search */}
            <div className="relative">
              <Search size={11} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                data-testid="library-search"
                type="text"
                placeholder="Search your tracks…"
                value={librarySearch}
                onChange={(e) => setLibrarySearch(e.target.value)}
                className="w-full bg-[#111] border border-white/10 rounded-lg pl-7 pr-3 py-1.5 text-white text-xs placeholder:text-zinc-600 focus:outline-none focus:border-[#00ff77]/30"
              />
            </div>

            {/* Track list */}
            <div className="max-h-48 overflow-y-auto space-y-1">
              {libraryLoading && (
                <div className="flex justify-center py-4">
                  <Loader2 size={16} className="animate-spin text-zinc-500" />
                </div>
              )}
              {!libraryLoading && filteredLibrary.length === 0 && (
                <p className="text-zinc-600 text-xs text-center py-3">
                  {library.length === 0 ? "No uploaded tracks yet." : "No tracks match your search."}
                </p>
              )}
              {filteredLibrary.map((track) => {
                const alreadyAdded = addedIds.has(track.id);
                return (
                  <div key={track.id} data-testid={`library-track-${track.id}`}
                    className="flex items-center gap-2 bg-[#111] rounded-lg px-2 py-1.5 group">
                    <Music size={10} className="text-zinc-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-xs truncate font-medium">{track.title || track.originalFilename}</p>
                      <p className="text-zinc-500 text-[10px] truncate">
                        {track.artist ? `${track.artist} · ` : ""}{formatTime(track.durationSeconds)}
                      </p>
                    </div>
                    <button
                      type="button"
                      data-testid={`add-from-library-${track.id}`}
                      onClick={() => addFromLibrary(track)}
                      disabled={alreadyAdded}
                      className={`flex-shrink-0 text-[10px] font-bold px-2.5 py-1 rounded-lg transition-colors ${
                        alreadyAdded
                          ? "bg-[#00ff77]/10 text-[#00ff77] cursor-default"
                          : "bg-white/10 text-zinc-300 hover:bg-[#00ff77] hover:text-black"
                      }`}
                    >
                      {alreadyAdded ? "Added ✓" : "+ Add"}
                    </button>
                  </div>
                );
              })}
            </div>

            <p className="text-zinc-700 text-[9px] text-center">
              Tracks added from library are reused — no re-upload needed
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function DashboardShows() {
  const { user } = useAuth();
  const [shows, setShows] = useState([]);
  const [stations, setStations] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [newShow, setNewShow] = useState({ title: "", description: "", stationId: "", dayOfWeek: 0, startTime: "12:00", tracks: [] });
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, [user]);

  const loadData = async () => {
    if (!user) return;
    try {
      const stationsQ = query(collection(db, "stations"), where("creatorId", "==", user.uid));
      const stationsSnap = await getDocs(stationsQ);
      const stationsData = stationsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setStations(stationsData);
      if (stationsData.length > 0 && !newShow.stationId) {
        setNewShow((n) => ({ ...n, stationId: stationsData[0].id }));
      }

      const showsQ = query(collection(db, "shows"), where("creatorId", "==", user.uid));
      const showsSnap = await getDocs(showsQ);
      const showsData = showsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      showsData.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setShows(showsData);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const hasOverlap = (existingShows, dayOfWeek, startTime, durationMinutes, excludeId) => {
    const [h, m] = startTime.split(":").map(Number);
    const startMins = h * 60 + m;
    const endMins = startMins + durationMinutes;

    return existingShows
      .filter((s) => s.dayOfWeek === dayOfWeek && s.id !== excludeId)
      .some((s) => {
        const [sh, sm] = s.startTime.split(":").map(Number);
        const sStart = sh * 60 + sm;
        const sDuration = Math.ceil((s.totalRuntime || 0) / 60);
        const sEnd = sStart + sDuration;
        return startMins < sEnd && endMins > sStart;
      });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newShow.stationId) { setError("Please select a station."); return; }
    if (newShow.tracks.length === 0) { setError("Add at least one track."); return; }

    const totalRuntime = newShow.tracks.reduce((acc, t) => acc + (t.durationSeconds || 0), 0);
    const durationMinutes = Math.ceil(totalRuntime / 60);

    // Check overlap
    const stationShows = shows.filter((s) => s.stationId === newShow.stationId);
    if (hasOverlap(stationShows, newShow.dayOfWeek, newShow.startTime, durationMinutes)) {
      setError("Schedule conflict! This show overlaps with an existing show.");
      return;
    }

    setSaving(true); setError("");
    try {
      const [h, m] = newShow.startTime.split(":").map(Number);
      const endMins = h * 60 + m + durationMinutes;
      const endH = Math.floor(endMins / 60) % 24;
      const endM = endMins % 60;
      const endTime = `${endH.toString().padStart(2, "0")}:${endM.toString().padStart(2, "0")}`;

      const showDoc = await addDoc(collection(db, "shows"), {
        ...newShow,
        creatorId: user.uid,
        totalRuntime,
        durationMinutes,
        endTime,
        status: "pending",
        createdAt: serverTimestamp(),
      });

      // Add to schedule
      await addDoc(collection(db, "schedules"), {
        stationId: newShow.stationId,
        showId: showDoc.id,
        showTitle: newShow.title,
        dayOfWeek: newShow.dayOfWeek,
        startTime: newShow.startTime,
        endTime,
        type: "regular",
        createdAt: serverTimestamp(),
      });

      setShows((s) => [{ id: showDoc.id, ...newShow, totalRuntime, durationMinutes, endTime }, ...s]);
      setNewShow({ title: "", description: "", stationId: stations[0]?.id || "", dayOfWeek: 0, startTime: "12:00", tracks: [] });
      setCreating(false);
    } catch (err) { setError(err.message); }
    setSaving(false);
  };

  const handleDeleteShow = async (id) => {
    if (!window.confirm("Delete this show and remove it from the schedule?")) return;
    await deleteDoc(doc(db, "shows", id));
    // Delete from schedules
    const schedQ = query(collection(db, "schedules"), where("showId", "==", id));
    const schedSnap = await getDocs(schedQ);
    for (const d of schedSnap.docs) await deleteDoc(d.ref);
    setShows((s) => s.filter((x) => x.id !== id));
  };

  const handleAddTrack = (track) => {
    setNewShow((n) => ({ ...n, tracks: [...n.tracks, track] }));
  };

  const handleRemoveTrack = (i) => {
    setNewShow((n) => ({ ...n, tracks: n.tracks.filter((_, idx) => idx !== i) }));
  };

  const totalRuntime = newShow.tracks.reduce((acc, t) => acc + (t.durationSeconds || 0), 0);

  if (loading) return <div className="flex justify-center py-10"><div className="w-6 h-6 border-2 border-[#B026FF] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="dashboard-shows" className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white">Radio Shows</h2>
        <button onClick={() => setCreating(!creating)} data-testid="create-show-btn"
          className="flex items-center gap-2 bg-[#B026FF] text-white font-bold text-xs px-4 py-2 rounded-full hover:bg-[#9b1ee6] transition-colors">
          <Plus size={14} /> New Show
        </button>
      </div>

      {stations.length === 0 && (
        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-4 text-sm text-yellow-400">
          You need a station first. <a href="/dashboard/station" className="underline">Create your station</a>.
        </div>
      )}

      {/* Create form */}
      {creating && (
        <div className="bg-[#111] border border-[#B026FF]/20 rounded-2xl p-5 space-y-4">
          <h3 className="text-white font-bold text-sm">Create New Show</h3>
          <form onSubmit={handleCreate} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <input required type="text" placeholder="Show title" value={newShow.title} onChange={(e) => setNewShow({ ...newShow, title: e.target.value })}
                className="bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#B026FF]/50" />
              <select value={newShow.stationId} onChange={(e) => setNewShow({ ...newShow, stationId: e.target.value })}
                className="bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#B026FF]/50">
                {stations.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <textarea placeholder="Description (optional)" value={newShow.description} onChange={(e) => setNewShow({ ...newShow, description: e.target.value })} rows={2}
              className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#B026FF]/50 resize-none" />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-zinc-500 text-xs mb-1 block">Day of Week</label>
                <select value={newShow.dayOfWeek} onChange={(e) => setNewShow({ ...newShow, dayOfWeek: parseInt(e.target.value) })}
                  className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#B026FF]/50">
                  {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
                </select>
              </div>
              <div>
                <label className="text-zinc-500 text-xs mb-1 block">Start Time</label>
                <input type="time" value={newShow.startTime} onChange={(e) => setNewShow({ ...newShow, startTime: e.target.value })}
                  className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#B026FF]/50" />
              </div>
            </div>

            {/* Tracks */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-zinc-400 text-xs font-semibold uppercase tracking-widest">Tracks</label>
                {newShow.tracks.length > 0 && (
                  <span className="text-xs text-[#B026FF]">Total: {formatTime(totalRuntime)}</span>
                )}
              </div>
              <div className="space-y-1.5 mb-2">
                {newShow.tracks.map((t, i) => (
                  <div key={i} className="flex items-center gap-2 bg-[#1a1a1a] rounded-lg px-3 py-2 text-xs">
                    <Music size={10} className="text-zinc-600" />
                    <span className="flex-1 text-zinc-300 truncate">{t.title}</span>
                    <span className="text-zinc-500">{formatTime(t.durationSeconds)}</span>
                    <button type="button" onClick={() => handleRemoveTrack(i)} className="text-zinc-600 hover:text-red-400">
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
              <TrackUploader onTrackAdded={handleAddTrack} />
            </div>

            {error && (
              <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-xl p-3">
                <AlertCircle size={14} className="text-red-400" />
                <p className="text-red-400 text-xs">{error}</p>
              </div>
            )}

            <div className="flex gap-2">
              <button type="submit" disabled={saving}
                className="bg-[#B026FF] text-white font-bold text-sm px-5 py-2 rounded-full hover:bg-[#9b1ee6] transition-colors disabled:opacity-50">
                {saving ? "Creating..." : "Create Show"}
              </button>
              <button type="button" onClick={() => { setCreating(false); setError(""); }}
                className="border border-white/10 text-zinc-400 text-sm px-5 py-2 rounded-full hover:border-white/30 transition-colors">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Shows list */}
      <div className="space-y-3">
        {shows.length === 0 ? (
          <div className="text-center py-16 text-zinc-500">
            <Music size={32} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm">No shows created yet.</p>
          </div>
        ) : (
          shows.map((show) => (
            <div key={show.id} data-testid={`show-item-${show.id}`}
              className="bg-[#111] border border-white/5 rounded-2xl overflow-hidden">
              <div
                className="flex items-center gap-3 p-4 cursor-pointer hover:bg-[#1a1a1a] transition-colors"
                onClick={() => setExpanded(expanded === show.id ? null : show.id)}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-white font-semibold text-sm">{show.title}</p>
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full border ${
                      show.status === "approved" ? "text-[#00ff77] bg-[#00ff77]/10 border-[#00ff77]/20" :
                      show.status === "rejected" ? "text-red-400 bg-red-400/10 border-red-400/20" :
                      "text-yellow-400 bg-yellow-400/10 border-yellow-400/20"
                    }`}>
                      {show.status === "approved" ? "Live" : show.status === "rejected" ? "Rejected" : "Pending"}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-zinc-500 text-xs mt-0.5">
                    <span>{DAYS[show.dayOfWeek]} at {show.startTime}</span>
                    <span className="flex items-center gap-1"><Clock size={10} /> {formatTime(show.totalRuntime || 0)}</span>
                    <span className="flex items-center gap-1"><Music size={10} /> {(show.tracks || []).length} tracks</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={(e) => { e.stopPropagation(); handleDeleteShow(show.id); }} data-testid={`delete-show-${show.id}`}
                    className="text-zinc-500 hover:text-red-400 p-1 rounded-lg hover:bg-red-400/10 transition-colors">
                    <Trash2 size={14} />
                  </button>
                  {expanded === show.id ? <ChevronUp size={16} className="text-zinc-500" /> : <ChevronDown size={16} className="text-zinc-500" />}
                </div>
              </div>
              {expanded === show.id && show.tracks && show.tracks.length > 0 && (
                <div className="border-t border-white/5 p-4 space-y-1.5">
                  {show.tracks.map((t, i) => (
                    <div key={i} className="flex items-center gap-3 text-xs text-zinc-400">
                      <span className="text-zinc-600 w-4 text-right">{i + 1}</span>
                      <Music size={10} className="text-zinc-600" />
                      <span className="flex-1 truncate">{t.title}</span>
                      <span className="text-zinc-500">{formatTime(t.durationSeconds)}</span>
                    </div>
                  ))}
                  <div className="pt-2 border-t border-white/5 flex justify-between text-xs">
                    <span className="text-zinc-500">Total Runtime</span>
                    <span className="text-[#B026FF] font-semibold">{formatTime(show.totalRuntime || 0)}</span>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
